import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { launchModal } from '@domain-group/javascript-utils'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import * as providerPriceListProductUnlockActions from '@redux/modules/providerPriceListProductUnlock'

import defaultFormState, { requiredFields } from '@models/providerPriceListProductUnlock'

import PageContext from '@contexts/pageContext'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams, ModuleState } from '@redux/modules/types'
import type { ProviderPriceListProductUnlockModel, ProviderPriceListProductUnlockRequestOptions } from '@models/types'

export const generateUrls = (providerPriceListProductUnlock?: Partial<ProviderPriceListProductUnlockModel>) => {
  const { id } = providerPriceListProductUnlock || {}

  return {
    editProviderPriceListProductUnlockDetailsUrl: `#/systemManager/providerPriceListProductUnlocks/${id}/edit/details`,
    editProviderPriceListProductUnlockUrl: `#/systemManager/providerPriceListProductUnlocks/${id}/edit/`,
    providerPriceListProductUnlocksIndexUrl: '#/systemManager/providerPriceListProductUnlocks',
  }
}

type CreateProviderPriceListProductUnlockParams = {
  dispatch: AppDispatch,
  providerPriceListProductUnlockParams: Partial<ProviderPriceListProductUnlockModel>,
  requestOptions?: ProviderPriceListProductUnlockRequestOptions,
}

const createProviderPriceListProductUnlock = (params: CreateProviderPriceListProductUnlockParams) => {
  const { dispatch, providerPriceListProductUnlockParams, requestOptions } = params
  const { createProviderPriceListProductUnlock: createFn } = providerPriceListProductUnlockActions

  return dispatch(createFn(providerPriceListProductUnlockParams, requestOptions))
}

type UpdateProviderPriceListProductUnlockParams = {
  dispatch: AppDispatch,
  providerPriceListProductUnlock: ProviderPriceListProductUnlockModel,
  providerPriceListProductUnlockParams: Partial<ProviderPriceListProductUnlockModel>,
  requestOptions?: ProviderPriceListProductUnlockRequestOptions,
}

const updateProviderPriceListProductUnlock = (params: UpdateProviderPriceListProductUnlockParams) => {
  const {
    dispatch, providerPriceListProductUnlock, providerPriceListProductUnlockParams, requestOptions,
  } = params
  const { updateProviderPriceListProductUnlock: updateFn } = providerPriceListProductUnlockActions

  const updatedParams = {
    id: providerPriceListProductUnlock.id,
    ...providerPriceListProductUnlockParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeleteProviderPriceListProductUnlockParams = {
  dispatch: AppDispatch,
  providerPriceListProductUnlock: DeleteParams<ProviderPriceListProductUnlockModel>,
}

const deleteProviderPriceListProductUnlock = (params: DeleteProviderPriceListProductUnlockParams) => {
  const { dispatch, providerPriceListProductUnlock } = params
  const { deleteProviderPriceListProductUnlock: deleteFn } = providerPriceListProductUnlockActions

  return dispatch(deleteFn(providerPriceListProductUnlock))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useProviderPriceListProductUnlockForm(
  providerPriceListProductUnlock: Partial<ProviderPriceListProductUnlockModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const providerPriceListProductUnlockForm = useForm(
    defaultFormState,
    {
      entity: providerPriceListProductUnlock,
      requiredFields: [...requiredFields, ...customRequiredFields],
      validateOn,
    },
    [providerPriceListProductUnlock.id, providerPriceListProductUnlock.cache_key],
  )

  return {
    ...providerPriceListProductUnlockForm,
  }
}

export const useRelations = (providerPriceListProductUnlock: Partial<ProviderPriceListProductUnlockModel> = {}) => {
  const {
    provider_price_list_id,
    service_product_id,
    unlock_price_list_id,
    unlock_product_id,
  } = providerPriceListProductUnlock

  const { providerPriceLists, serviceProducts } = useSelector(reduxState => reduxState.entities)

  const providerPriceList = provider_price_list_id && providerPriceLists[provider_price_list_id]
    ? providerPriceLists[provider_price_list_id] : {}
  const serviceProduct = service_product_id && serviceProducts[service_product_id]
    ? serviceProducts[service_product_id] : {}
  const unlockPriceList = unlock_price_list_id && providerPriceLists[unlock_price_list_id]
    ? providerPriceLists[unlock_price_list_id] : {}
  const unlockProductId = unlock_product_id && serviceProducts[unlock_product_id]
    ? serviceProducts[unlock_product_id] : {}

  return {
    providerPriceList,
    serviceProduct,
    unlockPriceList,
    unlockProductId,
  }
}

type Options = {
  performHttpRequests?: boolean,
  requestOptions?: ProviderPriceListProductUnlockRequestOptions,
}

function useProviderPriceListProductUnlock(
  initEntity: Partial<ProviderPriceListProductUnlockModel> = {},
  options: Options = {},
) {
  const { performHttpRequests, requestOptions } = options

  // @ts-expect-error TS2322
  const { entity: providerPriceListProductUnlock }: { entity: ProviderPriceListProductUnlockModel} = useLatestEntity(
    initEntity,
    'providerPriceListProductUnlocks',
  )
  const { id } = providerPriceListProductUnlock

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const { providerPriceList, unlockPriceList } = useRelations(providerPriceListProductUnlock)

  useReduxAction(
    'providerPriceListProductUnlocks',
    'loadProviderPriceListProductUnlock',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  const {
    creating, deleting, loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.providerPriceListProductUnlocks)

  return {
    callbacks: {
      createProviderPriceListProductUnlock: (
        providerPriceListProductUnlockParams: Partial<ProviderPriceListProductUnlockModel>,
        entityOptions?: ProviderPriceListProductUnlockRequestOptions,
      ) => (
        createProviderPriceListProductUnlock({
          providerPriceListProductUnlockParams,
          dispatch,
          requestOptions: entityOptions,
        })
      ),
      createOrEditProviderPriceListProductUnlock: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditProviderPriceListProductUnlockModal',
        payload: { providerPriceListProductUnlock },
      }),
      deleteProviderPriceListProductUnlock: () => (
        deleteProviderPriceListProductUnlock({ dispatch, providerPriceListProductUnlock })
      ),
      updateProviderPriceListProductUnlock: (
        providerPriceListProductUnlockParams: Partial<ProviderPriceListProductUnlockModel>,
        entityOptions?: ProviderPriceListProductUnlockRequestOptions,
      ) => (
        updateProviderPriceListProductUnlock({
          providerPriceListProductUnlock, providerPriceListProductUnlockParams, dispatch, requestOptions: entityOptions,
        })
      ),
    },
    creating,
    deleting,
    loading: loading && !loadedIds.includes(providerPriceListProductUnlock.id),
    providerPriceList,
    providerPriceListProductUnlock,
    unlockPriceList,
    updating,
    urls: generateUrls(providerPriceListProductUnlock),
  }
}

export default useProviderPriceListProductUnlock
